/* Базы блоков, тренировок и программ (TPL) — одна витрина (ui.js renderLib):
   своё выше общего, внутри своего — новое первым, общее — по частоте использования.
   У блоков есть папки и типы и создание прямо в базе (TPL-1). */
import { trpc, useCatalog } from '@tg/app-core';
import { BLOCK_TYPES, blockCount, blockName, blockTypeLabel, chainScheme, fmtLabel, FMT_TYPES, isTextBlock, lineName, loadText, mkBlock, mkLine, norm, normFmt, plural3 as plural, textLines, TPL_FOLDERS, TYPE_FOLDER, typeName, type Block, type BlockKind, type ComplexFormat, type FormatKind, type Line, type WorkoutContent } from '@tg/domain';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AssignProgram } from './Program';
import { useTemplates, type Template, type TplLevel } from '../lib/data';
import { parseNum } from '../lib/format';
import { RailLayout, usePageTitle } from '../shell/Shell';
import { byNum, byStr, DataTable, type Col } from '../ui/DataTable';
import { ICON } from '../ui/icons';
import { Modal } from '../ui/Modal';
import { toast } from '../ui/Toast';

const CFG: Record<TplLevel, { title: string; kind: string; ph: string; add: string }> = {
  block: { title: 'Блоки', kind: 'Блок', ph: 'Поиск блока…', add: 'Создать блок' },
  /* Не «Создать тренировку» — так же называется кнопка в шапке, но та сразу назначает
     тренировку клиенту/группе (открывает конструктор); эта — кладёт шаблон в базу,
     без назначения кому-либо. */
  workout: { title: 'Тренировки', kind: 'Тренировка', ph: 'Поиск тренировки…', add: 'Создать шаблон тренировки' },
  program: { title: 'Программы', kind: 'Программа', ph: 'Поиск программы…', add: 'Создать программу' },
};
const LIB = { q: '', own: false, folder: 'Все', type: 'all', sel: {} as Record<string, string | null> };

type Ex = ReturnType<typeof useCatalog>['ex'];
/** Шаблон с разобранным содержимым — для подписей и статистики */
interface Row {
  id: string;
  t: Template;
  title: string;
  own: boolean;
  block: Block | null;
  workout: WorkoutContent | null;
  seq: (WorkoutContent | null)[];
  days: number;
}
function toRow(t: Template): Row {
  const c = t.content as any;
  const block = t.level === 'block' && c ? normFmt(JSON.parse(JSON.stringify(c)) as Block) : null;
  const title = block && t.title ? stripTitle(t.title) : t.title;
  return { id: t.id, t, title, own: !!t.trainerId, block, workout: t.level === 'workout' ? (c as WorkoutContent) : null, seq: t.level === 'program' ? c?.seq || [] : [], days: t.level === 'program' ? c?.days || c?.seq?.length || 0 : 0 };
}
/* Формат из названия переезжает в тип блока, название — чистое (normFmt) */
const stripTitle = (s: string) => normFmt(mkBlock({ title: s })).title || s;

const lineText = (y: Line, ex: Ex) => {
  if (y.t === 'chain') {
    const prm = [loadText(y.load), chainScheme(y)].filter(Boolean).join(' — ');
    return lineName(y, ex) + (prm ? ': ' + prm : '');
  }
  const p = y.scheme || y.txt || (y.load && y.load.kind === 'abs' ? loadText(y.load) : '');
  return (lineName(y, ex) || '—') + (p ? ' · ' + p : '');
};
const ssLabel = (rounds: number, rest?: string) => 'Сет · ' + rounds + ' ' + plural(rounds, 'круг', 'круга', 'кругов') + (rest ? ' · отдых ' + rest : '');

/** Состав строками: у блока — упражнения, у тренировки — блоки, у программы — цель и цикл */
function libLines(r: Row, ex: Ex): { cls: string; text: string }[] {
  if (r.block) {
    if (isTextBlock(r.block)) return textLines(r.block.text).map((l) => ({ cls: '', text: l }));
    return r.block.items.flatMap((y) => (y.t === 'set' ? [{ cls: 'ssl', text: ssLabel(y.rounds, y.rest) }, ...y.items.map((l) => ({ cls: 'sub', text: lineText(l, ex) }))] : [{ cls: '', text: lineText(y, ex) }]));
  }
  if (r.workout) return r.workout.blocks.map((b) => ({ cls: '', text: blockName(b) || 'блок' }));
  const q = r.seq.map((w) => (w ? w.title || 'тренировка' : 'отдых'));
  return [
    { cls: '', text: r.t.goal || '' },
    { cls: '', text: 'цикл: ' + q.join(' → ') },
  ];
}
function stats(r: Row) {
  if (r.block) return { n: blockCount(r.block), blocks: 0, cycle: 0, workouts: 0 };
  if (r.workout) return { n: r.workout.blocks.reduce((a, b) => a + blockCount(b), 0), blocks: r.workout.blocks.length, cycle: 0, workouts: 0 };
  return { n: 0, blocks: 0, cycle: r.seq.length, workouts: r.seq.filter(Boolean).length };
}
const libSort = (a: Row, b: Row) => (a.own !== b.own ? (a.own ? -1 : 1) : a.own ? String(b.t.updatedAt).localeCompare(String(a.t.updatedAt)) : (b.t.used || 0) - (a.t.used || 0));
const typeOf = (r: Row) => (r.block ? blockTypeLabel(r.block) : '');

export function Library({ level }: { level: TplLevel }) {
  const cfg = CFG[level];
  const tq = useTemplates(level);
  const cat = useCatalog();
  usePageTitle(cfg.title, tq.data?.length ?? null);
  const [, redraw] = useState(0);
  const set = (f: () => void) => {
    f();
    redraw((x) => x + 1);
  };
  const [adding, setAdding] = useState(false);
  const [assign, setAssign] = useState<Template | null>(null);
  const nav = useNavigate();
  const isProg = level === 'program';
  const all = (tq.data || []).map(toRow);
  const withFolders = level === 'block';
  const list = all
    .filter((r) => {
      if (LIB.own && !r.own) return false;
      if (withFolders && LIB.folder !== 'Все' && r.t.folder !== LIB.folder) return false;
      if (withFolders && LIB.type !== 'all' && (r.block?.kind || 'none') !== LIB.type) return false;
      if (LIB.q && norm(r.title).indexOf(norm(LIB.q)) < 0) return false;
      return true;
    })
    .sort(libSort);
  /* Тап по строке на телефоне открывает шторку с превью (RailLayout sheetKey) */
  const [tap, setTap] = useState(0);
  let sel = LIB.sel[level] ?? null;
  if (!list.some((r) => r.id === sel)) sel = LIB.sel[level] = list.length ? list[0].id : null;

  const nameCol: Col<Row> = {
    k: 'title',
    n: 'Название',
    sort: byStr((r) => r.title),
    cell: (r) => (
      <div className="cellname">
        <div style={{ minWidth: 0 }}>
          <div className="n">{r.title}</div>
          <div className="s">
            {libLines(r, cat.ex)
              .map((l) => l.text)
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
      </div>
    ),
  };
  const ownCol: Col<Row> = { k: 'own', n: 'Источник', w: '110px', sort: byNum((r) => (r.own ? 0 : 1)), cell: (r) => (r.own ? <span className="chip ok">своё</span> : <span className="chip ghost">общая</span>) };
  const usedCol: Col<Row> = { k: 'used', n: 'Исп.', w: '70px', r: true, num: true, sort: byNum((r) => r.t.used), cell: (r) => (r.t.used ? r.t.used + '×' : '—') };
  const cols: Col<Row>[] =
    level === 'block'
      ? [
          nameCol,
          { k: 'kind', n: 'Тип', w: '200px', sort: (a, b) => typeOf(a).localeCompare(typeOf(b), 'ru'), cell: (r) => (r.block?.kind ? typeOf(r) : <span style={{ color: 'var(--tx3)' }}>—</span>) },
          { k: 'folder', n: 'Папка', w: '140px', sort: byStr((r) => r.t.folder), cell: (r) => r.t.folder || '—' },
          { k: 'n', n: 'Упр.', w: '70px', r: true, num: true, sort: byNum((r) => stats(r).n), cell: (r) => stats(r).n },
          ownCol,
          usedCol,
        ]
      : level === 'workout'
        ? [
            nameCol,
            { k: 'blocks', n: 'Блоков', w: '80px', r: true, num: true, sort: byNum((r) => stats(r).blocks), cell: (r) => stats(r).blocks },
            { k: 'n', n: 'Упр.', w: '70px', r: true, num: true, sort: byNum((r) => stats(r).n), cell: (r) => stats(r).n },
            ownCol,
            usedCol,
          ]
        : [
            nameCol,
            { k: 'days', n: 'Дней', w: '80px', r: true, num: true, sort: byNum((r) => r.days), cell: (r) => r.days },
            { k: 'cycle', n: 'Цикл', w: '80px', r: true, num: true, sort: byNum((r) => stats(r).cycle), cell: (r) => stats(r).cycle },
            { k: 'wo', n: 'Трен.', w: '75px', r: true, num: true, sort: byNum((r) => stats(r).workouts), cell: (r) => stats(r).workouts },
            ownCol,
            usedCol,
            {
              k: 'act',
              n: '',
              w: '130px',
              r: true,
              cell: (r) =>
                stats(r).workouts ? (
                  <button
                    className="btn gh sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAssign(r.t);
                    }}
                  >
                    Назначить
                  </button>
                ) : null,
            },
          ];

  const r = all.find((x) => x.id === sel);
  let body: ReactNode, foot: ReactNode = '';
  if (!r)
    body = (
      <div className="rprev">
        <div className="hint">Выберите строку — здесь появится карточка.</div>
      </div>
    );
  else {
    const st = stats(r);
    const dim = (s: string) => <span style={{ color: 'var(--tx3)' }}>{s}</span>;
    const facts =
      level === 'block' ? (
        <>
          <s>Тип</s>
          <b>{r.block?.kind ? typeName(r.block.kind) : dim('без типа')}</b>
          {r.block?.kind === 'complex' && (
            <>
              <s>Настройка</s>
              <b>{r.block.fmt ? fmtLabel(r.block.fmt) : dim('без настройки')}</b>
            </>
          )}
          <s>Папка</s>
          <b>{r.t.folder || '—'}</b>
          <s>Упражнений</s>
          <b>{st.n}</b>
        </>
      ) : level === 'workout' ? (
        <>
          <s>Блоков</s>
          <b>{st.blocks}</b>
          <s>Упражнений</s>
          <b>{st.n}</b>
        </>
      ) : (
        <>
          <s>Дней</s>
          <b>{r.days}</b>
          <s>Цикл</s>
          <b>{st.cycle} дн.</b>
          <s>Тренировок</s>
          <b>{st.workouts}</b>
        </>
      );
    body = (
      <div className="rprev">
        <b className="rt">{r.title}</b>
        <div className="kv">
          {facts}
          <s>Источник</s>
          <b>{r.own ? <span className="chip ok">своё</span> : 'общая база'}</b>
          <s>Использован</s>
          <b>{r.t.used ? r.t.used + '×' : '—'}</b>
        </div>
        <div className="rhead" style={{ paddingLeft: 0 }}>
          {level === 'program' ? 'Цель и цикл' : 'Состав'}
        </div>
        <div className="rlines">
          {libLines(r, cat.ex).map((l, i) => (
            <span key={i} className={l.cls}>
              {l.text}
            </span>
          ))}
        </div>
        <div className="acts">
          <Link className="btn sm" to={'/constructor?tpl=' + r.id}>
            <ICON.build /> Вставить в тренировку
          </Link>
          {r.own && (
            <button className="btn gh sm" id="rEdit" onClick={() => toast('Редактирование шаблона — в конструкторе: вставьте, измените и сохраните')}>
              Изменить
            </button>
          )}
        </div>
      </div>
    );
    foot = r.own ? '' : 'Шаблон из общей базы: изменить нельзя — вставьте в тренировку и сохраните как свой.';
  }

  const page = (
    <>
        <div className="pane-h">
          <label className="search" style={{ width: 260 }}>
            <ICON.search />
            <input id="q" placeholder={cfg.ph} autoFocus value={LIB.q} onChange={(e) => set(() => (LIB.q = e.target.value))} />
          </label>
          {withFolders && (
            <>
              <select className="inp" id="folder" style={{ width: 160 }} value={LIB.folder} onChange={(e) => set(() => (LIB.folder = e.target.value))}>
                {/* Только папки, в которых реально что-то есть — иначе на пустой базе (у нового
                    тренера блоки больше не общие) список предлагал бы категории без единого блока */}
                {['Все', ...TPL_FOLDERS.filter((f) => all.some((r) => r.t.folder === f))].map((f) => (
                  <option key={f} value={f}>
                    {f === 'Все' ? 'Все папки' : f}
                  </option>
                ))}
              </select>
              <select className="inp" id="btype" style={{ width: 160 }} value={LIB.type} onChange={(e) => set(() => (LIB.type = e.target.value))}>
                {[['all', 'Все типы'], ...BLOCK_TYPES.map((t) => [t.k || 'none', t.k ? t.n : 'Без типа'])].map(([v, n]) => (
                  <option key={v} value={v}>
                    {n}
                  </option>
                ))}
              </select>
            </>
          )}
          <label className="chk">
            <input type="checkbox" id="own" checked={LIB.own} onChange={(e) => set(() => (LIB.own = e.target.checked))} /> Только свои
          </label>
          <span className="sp"></span>
          <button className={'btn' + (isProg ? '' : ' gh')} id="btnAdd" onClick={() => (level === 'block' ? setAdding(true) : isProg ? nav('/programs/new') : toast('В конструкторе: соберите и нажмите «Сохранить в библиотеку»'))}>
            <ICON.plus /> {cfg.add}
          </button>
        </div>
        {!tq.data ? null : list.length ? (
          <DataTable id={'lib-' + level} cols={cols} rows={list} sel={sel} onRow={(x) => (isProg ? nav('/programs/' + x.id) : (set(() => (LIB.sel[level] = x.id)), setTap((n) => n + 1)))} fixed />
        ) : (
          <div className="empty">
            <div className="t">Ничего не нашли</div>
            <div className="d">Измените поиск или фильтр</div>
          </div>
        )}
      {adding && <NewBlock onClose={() => setAdding(false)} onSaved={(id) => set(() => (LIB.sel.block = id))} />}
      {assign && <AssignProgram tpl={assign} onClose={() => setAssign(null)} />}
    </>
  );
  /* Программы — без боковой панели (как в прототипе): строка открывает страницу программы, там все действия */
  if (isProg)
    return (
      <div className="cons one">
        <main>
          <section className="pane" id="page">
            {page}
          </section>
        </main>
      </div>
    );
  return (
    <RailLayout minLabel={cfg.kind} title={cfg.kind} body={body} foot={foot} sheetKey={tap ? String(tap) : null}>
      {page}
    </RailLayout>
  );
}

/* ─── «Создать блок» (TPL-1): название, тип, папка; у комплекса — настройка с одним
   параметром; состав строками «упражнение · схема», упражнение — из базы по имени. ─── */
const NB_PARAM: Partial<Record<FormatKind, [string, (f: Omit<ComplexFormat, 'k'>) => number | string]>> = {
  AMRAP: ['Минут', (f) => f.total / 60],
  EMOM: ['Минут', (f) => f.rounds],
  FOR_TIME: ['Лимит, мин · необязательно', (f) => (f.total ? f.total / 60 : '')],
  TABATA: ['Раундов', (f) => f.rounds],
  INTERVALS: ['Раундов', (f) => f.rounds],
};
function nbFmtOf(k: FormatKind, p: string): ComplexFormat | null {
  const t = FMT_TYPES.find((x) => x.k === k);
  if (!t) return null;
  const f: ComplexFormat = { k: t.k, ...t.d };
  const n = parseNum(p);
  if (isFinite(n) && n > 0) {
    if (k === 'AMRAP') {
      f.total = Math.round(n * 60);
      f.work = f.total;
    } else if (k === 'EMOM') {
      f.rounds = Math.round(n);
      f.total = f.rounds * f.work;
    } else if (k === 'FOR_TIME') f.total = Math.round(n * 60);
    else if (k === 'TABATA' || k === 'INTERVALS') {
      f.rounds = Math.round(n);
      f.total = f.rounds * (f.work + f.rest);
    }
  }
  return f;
}

function NewBlock({ onClose, onSaved }: { onClose: () => void; onSaved: (id: string) => void }) {
  const cat = useCatalog();
  const utils = trpc.useUtils();
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<BlockKind | ''>('');
  const [folder, setFolder] = useState<string>(TPL_FOLDERS[0]);
  const [folderTouched, setFolderTouched] = useState(false);
  const [fmtK, setFmtK] = useState<FormatKind>(FMT_TYPES[0].k);
  const [param, setParam] = useState<string>(String(NB_PARAM[FMT_TYPES[0].k]![1](FMT_TYPES[0].d)));
  const [items, setItems] = useState([
    { ex: '', sch: '' },
    { ex: '', sch: '' },
  ]);
  const m = trpc.trainer.saveTemplate.useMutation({
    onSuccess: (t) => {
      utils.trainer.templates.invalidate({ level: 'block' });
      toast('Блок «' + t.title + '» создан');
      onSaved(t.id);
      onClose();
    },
    onError: (e) => toast(e.message || 'Не сохранилось'),
  });
  const resolve = (v: string) => {
    const q = norm(v);
    if (!q) return null;
    return cat.list.find((x) => norm(x.ru) === q) || cat.list.find((x) => norm(x.en || '') === q) || cat.matcher.match(v);
  };
  const pd = NB_PARAM[fmtK];
  const save = () => {
    if (!title.trim()) return toast('Дайте блоку название');
    const lines: Line[] = [],
      missing: string[] = [];
    for (const r of items) {
      const v = r.ex.trim();
      if (!v) continue;
      const e = resolve(v);
      if (!e) missing.push(v);
      else lines.push({ ...mkLine(e.id), scheme: r.sch.trim() || undefined });
    }
    if (missing.length) return toast('Не нашли в базе: ' + missing.join(', '));
    if (!lines.length) return toast('Добавьте хотя бы одно упражнение');
    const k = (kind || null) as BlockKind | null;
    const block = mkBlock({ title: title.trim(), kind: k, fmt: k === 'complex' ? nbFmtOf(fmtK, param) : null, items: lines });
    m.mutate({ level: 'block', title: title.trim(), folder, kind: k, content: block });
  };
  return (
    <Modal
      title="Новый блок"
      onClose={onClose}
      foot={
        <>
          <span className="sp"></span>
          <button className="btn gh" onClick={onClose}>
            Отмена
          </button>
          <button className="btn" id="nbSave" disabled={m.isPending} onClick={save}>
            Создать блок
          </button>
        </>
      }
    >
      <div className="cols3">
        <div className="formfld">
          <span className="k">Название</span>
          <input className="inp" id="nbTitle" autoFocus placeholder="Например: Разминка перед приседом" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="formfld">
          <span className="k">Тип блока</span>
          <select
            className="inp"
            id="nbType"
            value={kind}
            onChange={(e) => {
              const k = e.target.value as BlockKind | '';
              setKind(k);
              if (!folderTouched && k) setFolder(TYPE_FOLDER[k]);
            }}
          >
            {BLOCK_TYPES.map((t) => (
              <option key={t.k || ''} value={t.k || ''}>
                {t.n}
              </option>
            ))}
          </select>
        </div>
        <div className="formfld">
          <span className="k">Папка</span>
          <select
            className="inp"
            id="nbFolder"
            value={folder}
            onChange={(e) => {
              setFolder(e.target.value);
              setFolderTouched(true);
            }}
          >
            {TPL_FOLDERS.map((f) => (
              <option key={f}>{f}</option>
            ))}
          </select>
        </div>
      </div>
      {kind === 'complex' && (
        <div className="cols3" id="nbCxRow" style={{ marginTop: 12 }}>
          <div className="formfld">
            <span className="k">Настройка комплекса</span>
            <select
              className="inp"
              id="nbFmt"
              value={fmtK}
              onChange={(e) => {
                const k = e.target.value as FormatKind;
                setFmtK(k);
                const t = FMT_TYPES.find((x) => x.k === k)!;
                const p = NB_PARAM[k];
                setParam(p ? String(p[1](t.d)) : '');
              }}
            >
              {FMT_TYPES.map((t) => (
                <option key={t.k} value={t.k}>
                  {t.n}
                </option>
              ))}
            </select>
          </div>
          {pd && (
            <div className="formfld" id="nbParamFld">
              <span className="k">{pd[0]}</span>
              <input className="inp" id="nbParam" value={param} onChange={(e) => setParam(e.target.value)} />
            </div>
          )}
        </div>
      )}
      <div className="formfld" style={{ marginTop: 14 }}>
        <span className="k">Состав</span>
        <div id="nbItems">
          {items.map((r, i) => (
            <div className="nb-row" key={i}>
              <input className="inp" list="nbEx" placeholder="Упражнение" autoFocus={i > 1 && i === items.length - 1} value={r.ex} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, ex: e.target.value } : x)))} />
              <input className="inp" placeholder="3×5 · 10 · 500 м" value={r.sch} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, sch: e.target.value } : x)))} />
              <button className="iact" title="Убрать" onClick={() => items.length > 1 && setItems(items.filter((_, j) => j !== i))}>
                ✕
              </button>
            </div>
          ))}
        </div>
        <div>
          <button className="btn gh sm" id="nbAddRow" onClick={() => setItems([...items, { ex: '', sch: '' }])}>
            <ICON.plus /> Упражнение
          </button>
        </div>
      </div>
      <datalist id="nbEx">
        {[...cat.list]
          .sort((a, b) => a.ru.localeCompare(b.ru, 'ru'))
          .map((e) => (
            <option key={e.id} value={e.ru}></option>
          ))}
      </datalist>
    </Modal>
  );
}
